import { inject } from 'vue';
import { z } from 'zod';

/** Unified Legacy Adnet API interface — single source of truth */
export type LegacyAdnetApi = {
  get: (path: string, config?: { params?: Record<string, unknown>; signal?: AbortSignal }) => Promise<unknown>;
  post: (path: string, payload: unknown) => Promise<unknown>;
  put: (path: string, payload: unknown) => Promise<unknown>;
  delete: (path: string) => Promise<unknown>;
  postByUser: (path: string, payload: unknown, opts: { token: string; roleId: number }) => Promise<unknown>;
};

/** Response shape from legacy POST /dsp/creatives */
export type LegacyCreativeCreateResponse = {
  id?: number;
  result?: {
    id?: number;
  };
};

/** Unified Legacy Auth API interface */
export type LegacyAuthApi = {
  post: (path: string) => Promise<{ accessToken: string }>;
  request: (
    method: string,
    path: string,
    payload: Record<string, unknown>,
    options: { token: string; roleId: number },
  ) => Promise<{ grants?: Array<number> }>;
};

type LegacyApiContainer = {
  adnet?: unknown;
  auth?: unknown;
};

const LegacyArrayWrapperSchema = z.object({ data: z.array(z.unknown()) });
const LegacyResultWrapperSchema = z.object({ result: z.array(z.unknown()) });

const warnLegacyValidation = (label: string, fn: string, error: z.ZodError, fallback: string): void => {
  const issues = error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ');
  // eslint-disable-next-line no-console -- intentional: legacy API shape mismatch warning for developers
  console.warn(`[LegacyAPI] ${label} (${fn}): ${issues} → ${fallback}`);
};

/**
 * Composable that retrieves the legacy API clients injected by the application bootstrap.
 * Must be called inside a Vue component setup context.
 * The injection point is established in src/api/index.js via app.provide('api', api).
 *
 * Pass a type argument to narrow the `adnet` type for consumers that use a subset of the API:
 * ```ts
 * const { adnet: legacyApi, auth: legacyAuthApi } = useLegacyApi<LegacyCreativeApi>();
 * ```
 * Without a type argument, `adnet` defaults to `LegacyAdnetApi`.
 */
export const useLegacyApi = <TAdnet = LegacyAdnetApi, TAuth = LegacyAuthApi>(): {
  adnet: TAdnet | undefined;
  auth: TAuth | undefined;
} => {
  const container = inject<LegacyApiContainer | undefined>('api');
  return {
    adnet: container?.adnet as TAdnet | undefined,
    auth: container?.auth as TAuth | undefined,
  };
};

export const requireLegacyApi = <T>(api: T | undefined, name = 'Legacy API'): T => {
  if (!api) {
    throw new Error(`${name} not available`);
  }
  return api;
};

// DEV-only warnings: element-level mismatches are noisy (one per item) and expected
// during API evolution. Wrapper-shape failures (warnLegacyValidation) log in all
// environments because they indicate structural API contract changes.
const validateElements = <T>(
  rawArray: Array<unknown>,
  elementSchema: z.ZodType<T>,
  label: string,
  fn: string,
): Array<T> =>
  rawArray.flatMap((item, index) => {
    const result = elementSchema.safeParse(item);
    if (result.success) {
      return [result.data];
    }
    if (import.meta.env.DEV && label) {
      const issues = result.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ');
      // eslint-disable-next-line no-console -- intentional: element schema mismatch warning for developers
      console.warn(`[LegacyAPI] ${label} (${fn}): element[${index}] invalid — ${issues} → skipped`);
    }
    return [];
  });

export const extractLegacyArray = <T>(response: unknown, label?: string, elementSchema?: z.ZodType<T>): Array<T> => {
  if (Array.isArray(response)) {
    const rawArray = response as Array<unknown>;
    if (elementSchema) {
      return validateElements(rawArray, elementSchema, label ?? '', 'extractLegacyArray');
    }
    return rawArray as Array<T>;
  }

  const parsed = LegacyArrayWrapperSchema.safeParse(response);
  if (parsed.success) {
    if (elementSchema) {
      return validateElements(parsed.data.data, elementSchema, label ?? '', 'extractLegacyArray');
    }
    return parsed.data.data as Array<T>;
  }

  if (label) {
    warnLegacyValidation(label, 'extractLegacyArray', parsed.error, 'returning []');
  }

  return [];
};

export const extractLegacyResult = <T>(response: unknown, label?: string, elementSchema?: z.ZodType<T>): Array<T> => {
  const parsed = LegacyResultWrapperSchema.safeParse(response);
  if (parsed.success) {
    if (elementSchema) {
      return validateElements(parsed.data.result, elementSchema, label ?? '', 'extractLegacyResult');
    }
    return parsed.data.result as Array<T>;
  }

  if (label) {
    warnLegacyValidation(label, 'extractLegacyResult', parsed.error, 'returning []');
  }

  return [];
};
